import { FC, useEffect } from "react";

import {
  getGroupIdFromAccessId,
  getStudentIdFromAccessId,
  isGroupAccessId,
  isStudentAccessId,
} from "../../../../shared/access-id";
import { usePresentation } from "../../../../utils/presentation";
import { StudentDropdown } from "../StudentDropdown";
import { ExpandableOutcomes } from "../outcomes/ExpandableOutcomes";

import { ASSESSMENT_VIEW_QUERY_PARAM, SUBMISSION_ID_QUERY_PARAM } from "components/constants";
import { ClassResponsesDropdown } from "components/materials/page/ClassResponsesDropdown";
import { PageSecondarySidebar } from "components/materials/page/PageSecondarySidebar";
import { SubmissionMenu } from "components/materials/page/SubmissionMenu";
import { FeatureResponsesButton } from "components/materials/presentation/featuring/FeatureResponsesButton";
import { useIsAssessing } from "components/materials/section/assessment/hooks";
import { PageType } from "components/server-types";
import { t } from "i18n/i18n";
import { IconText } from "mds/components/IconText";
import { Menu, MenuDivider, MenuItem } from "mds/components/Menu";
import { SquareIconButton } from "mds/components/buttons/SquareIconButton";
import { useIsMdOrLarger } from "mds/hooks/use-responsive";
import { DotMenuVerticalIcon, PageIcon } from "mds/icons";
import { type PageTabVariant } from "providers/DocViewProvider";
import { useCapabilities } from "store/capability-hooks";
import { storeApi, useAppSelector } from "store/index";
import {
  selectAssessmentBySubmissionId,
  selectEnabledSectionUsersByPageId,
  selectFullSubmissionById,
  selectIsEditModeEnabled,
  selectNestedPageOutcomes,
} from "store/selectors";
import { toastSuccessMessage } from "utils/alerts";
import { isGroupPage } from "utils/page-utils";
import { useQueryParamsManager } from "utils/routing";

type PageWorksheetHeaderProps = {
  page: PageType;
  selectedTabVariant?: PageTabVariant;
  selectedAccessId?: string | null;
};

export const PageWorksheetHeader: FC<PageWorksheetHeaderProps> = ({
  page,
  selectedTabVariant,
  selectedAccessId,
}) => {
  const { getQueryParam, setQueryParams } = useQueryParamsManager();
  const isEditModeEnabled = useAppSelector(selectIsEditModeEnabled);
  const isSmallScreen = !useIsMdOrLarger();
  const { canAuthorSection } = useCapabilities();
  const isAssessing = useIsAssessing();

  const pageOutcomes = useAppSelector((s) => selectNestedPageOutcomes(s, page.id));

  const submissionId = getQueryParam(SUBMISSION_ID_QUERY_PARAM);
  const currentSubmission = useAppSelector((s) => selectFullSubmissionById(s, submissionId));
  const assessment =
    useAppSelector((s) => selectAssessmentBySubmissionId(s, currentSubmission?.id)) || null;
  const { hasProjectorView } = usePresentation();

  const sectionUsers = useAppSelector((s) => selectEnabledSectionUsersByPageId(s, page.id));

  const canSeeOutcomes = !isAssessing && (pageOutcomes.length > 0 || isEditModeEnabled);
  const canSeeSubmissionMenu =
    selectedTabVariant === "my_submission" ||
    selectedTabVariant === "student_submission" ||
    (!hasProjectorView &&
      canAuthorSection &&
      selectedTabVariant === "responses" &&
      selectedAccessId);

  const canSeeClassResponsesDropdown = selectedTabVariant === "responses";
  const canSeeStudentResponsesDropdown = selectedTabVariant === "student_submission";

  // Show 3-dot menu when instructor is viewing a student or group's page (draft or submission)
  const canSeeSubmissionActionsMenu =
    canAuthorSection &&
    selectedAccessId &&
    (isStudentAccessId(selectedAccessId) || isGroupAccessId(selectedAccessId));

  const handleAssessSubmission = () => {
    if (currentSubmission) {
      setQueryParams({
        [ASSESSMENT_VIEW_QUERY_PARAM]: "1",
        [SUBMISSION_ID_QUERY_PARAM]: currentSubmission.id,
      });
    }
  };

  const handleCollectSubmission = async () => {
    let options;
    if (isGroupPage(page) && selectedAccessId && isGroupAccessId(selectedAccessId)) {
      const pageGroupId = getGroupIdFromAccessId(selectedAccessId);
      options = { page_group_id: pageGroupId };
    } else if (selectedAccessId && isStudentAccessId(selectedAccessId)) {
      const userId = getStudentIdFromAccessId(selectedAccessId);
      const sectionUser = sectionUsers.find((cu) => cu.user_id === userId);
      if (!sectionUser) {
        return;
      }
      options = { section_user_id: sectionUser.id };
    } else {
      return;
    }

    const result = await storeApi.submissions.create({
      page_id: page.id,
      section_id: page.section_id,
      ...options,
    });

    if (result) {
      toastSuccessMessage(t("success.toasts.submission_success_one"));
      setQueryParams({ [SUBMISSION_ID_QUERY_PARAM]: result.id });
    }
  };

  useEffect(() => {
    storeApi.page_outcomes.list({ page_id: page.id });
  }, [page.interaction_mode, page.id]);

  useEffect(() => {
    if (currentSubmission?.id) {
      storeApi.assessments.list({ submission_id: currentSubmission.id });
    }
    if (assessment?.id) {
      storeApi.outcome_assessments.list({ assessment_id: assessment.id });
    }
  }, [currentSubmission?.id, assessment?.id]);

  return (
    <div className="mb-4 flex w-full max-w-[--page-default-max-width] flex-col gap-2">
      <div className="flex w-full flex-col flex-wrap gap-4">
        {canSeeOutcomes && <ExpandableOutcomes page={page} />}

        {(canSeeClassResponsesDropdown || canSeeSubmissionMenu) && (
          <div className="flex w-full items-center gap-2">
            {canSeeClassResponsesDropdown && (
              <ClassResponsesDropdown activelyPresented={false} pageId={page.id} />
            )}

            {canSeeStudentResponsesDropdown && (
              <StudentDropdown pageId={page.id} canAuthorSection />
            )}

            {canSeeSubmissionMenu && (
              <SubmissionMenu
                isAssessing={isAssessing}
                page={page}
                selectedAccessId={selectedAccessId}
                showDraft={selectedTabVariant === "responses"}
              />
            )}

            {canSeeSubmissionActionsMenu && (
              <Menu
                direction="bottom"
                menuButton={
                  <SquareIconButton
                    kind="tertiary"
                    size="s"
                    title={t("common.options")}
                    onClick={(e: React.MouseEvent) => {
                      e.preventDefault();
                      e.stopPropagation();
                    }}
                  >
                    <DotMenuVerticalIcon />
                  </SquareIconButton>
                }
                position="initial"
              >
                <MenuItem
                  disabled={!currentSubmission || isAssessing}
                  eventName="Student Page - Assess individual submission"
                  onClick={handleAssessSubmission}
                >
                  <IconText
                    iconStart={<PageIcon />}
                    text={t("submission_menu.assess_submission")}
                  />
                </MenuItem>
                <MenuDivider />
                <MenuItem
                  eventName="Student Page - Collect a response"
                  onClick={handleCollectSubmission}
                >
                  <IconText
                    iconStart={<PageIcon />}
                    text={t("submission_menu.collect_new_submission")}
                  />
                </MenuItem>
              </Menu>
            )}

            <FeatureResponsesButton className="ml-auto self-end" />
          </div>
        )}
      </div>
      {isSmallScreen && <PageSecondarySidebar page={page} />}
    </div>
  );
};
